import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setError("Aucune session de paiement fournie.");
      return;
    }
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/payments/status/${sessionId}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status !== "paid" && attempts < 12) {
          timer = setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attempts]);

  const paid = status?.payment_status === "paid";
  const failed = status?.payment_status === "failed";

  return (
    <div className="cs-cream min-h-[75vh] py-20" data-testid="page-payment-success">
      <div className="max-w-2xl mx-auto px-6">
        <div className="cs-eyebrow text-neutral-500">Paiement Stripe</div>
        <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">
          {paid ? "Merci, votre paiement est enregistré." : failed ? "Le paiement n'a pas abouti." : "Nous vérifions votre paiement…"}
        </h1>

        <div className="mt-8 cs-card p-6" data-testid="payment-status-card">
          {!status && !error && (
            <div className="flex items-center gap-3 text-sm">
              <Loader2 size={16} className="animate-spin" />
              <span>Interrogation du serveur de paiement…</span>
            </div>
          )}
          {status && (
            <div className="flex items-start gap-3">
              {paid ? (
                <CheckCircle2 size={20} className="text-[#0E7C90] mt-0.5" />
              ) : failed ? (
                <XCircle size={20} className="text-red-700 mt-0.5" />
              ) : (
                <Loader2 size={16} className="animate-spin mt-1" />
              )}
              <div className="text-sm">
                <div className="font-serif text-lg">
                  {status.reference ? `Commande ${status.reference}` : "Votre paiement"}
                </div>
                <div className="mt-1 text-neutral-700">
                  Montant : <span className="font-mono">{status.amount ? `${status.amount} €` : "—"}</span>
                </div>
                <div className="mt-1 font-mono text-[13px] text-neutral-500">
                  Statut Stripe : {status.payment_status}
                </div>
                {!paid && !failed && (
                  <div className="mt-3 text-[13px] text-neutral-600">
                    Votre projet va poursuivre son traitement dès la confirmation Stripe. Cette page se rafraîchit toute seule.
                  </div>
                )}
              </div>
            </div>
          )}
          {error && <div className="text-red-700 text-sm">{error}</div>}
        </div>

        <div className="mt-8 flex gap-3">
          <Link to="/espace" className="cs-btn-primary" data-testid="payment-back-to-space">Retour à mon espace</Link>
          {status?.order_id && (
            <Link to={`/espace/commande/${status.order_id}`} className="cs-btn-ghost !text-black !border-black" data-testid="payment-back-to-order">
              Voir ma commande
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
